import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowDownToLine, Loader2, CheckCircle2, XCircle,
  Clock, ChevronDown, RefreshCw, DollarSign, Filter
} from 'lucide-react';

const STATUS_STYLE = {
  pending:  'text-amber-500 bg-amber-500/10 border-amber-500/20',
  approved: 'text-blue-500  bg-blue-500/10  border-blue-500/20',
  paid:     'text-emerald-500 bg-emerald-500/10 border-emerald-500/20',
  rejected: 'text-red-500   bg-red-500/10   border-red-500/20',
};
const STATUS_ICON = {
  pending: Clock, approved: CheckCircle2, paid: CheckCircle2, rejected: XCircle,
};

const ACTIONS = {
  pending:  [{ action: 'approve', label: 'Approve', cls: 'bg-blue-500 text-white hover:bg-blue-600' },
             { action: 'reject',  label: 'Reject',  cls: 'border-red-500/40 text-red-500 hover:bg-red-500/10' }],
  approved: [{ action: 'mark_paid', label: 'Mark as Paid', cls: 'bg-emerald-600 text-white hover:bg-emerald-700' },
             { action: 'reject',    label: 'Reject',       cls: 'border-red-500/40 text-red-500 hover:bg-red-500/10' }],
};

export default function AdminPartnerWithdrawals() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [actionModal, setActionModal] = useState(null); // { wd, action }
  const [note, setNote] = useState('');
  const [actioning, setActioning] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/partner/admin/withdrawals`;
      if (statusFilter) url += `?status=${statusFilter}`;
      const { data } = await axios.get(url, { headers });
      setWithdrawals(data || []);
    } catch {
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  // Summary counts
  const counts = withdrawals.reduce((acc, w) => {
    acc[w.status] = (acc[w.status] || 0) + 1;
    acc.total_amount = (acc.total_amount || 0) + w.amount;
    return acc;
  }, {});

  const openAction = (wd, action) => {
    setActionModal({ wd, action });
    setNote('');
  };

  const handleAction = async () => {
    if (!actionModal) return;
    setActioning(true);
    try {
      const { data } = await axios.put(
        `${API}/partner/admin/withdrawals/${actionModal.wd.id}/action`,
        { action: actionModal.action, note },
        { headers }
      );
      setWithdrawals(prev => prev.map(w => w.id === data.id ? data : w));
      toast.success(`Withdrawal ${actionModal.action.replace('_', ' ')} successfully`);
      setActionModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActioning(false);
    }
  };

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  const pending  = withdrawals.filter(w => w.status === 'pending');
  const approved = withdrawals.filter(w => w.status === 'approved');
  const totalPending = pending.reduce((s, w) => s + w.amount, 0);
  const totalApproved = approved.reduce((s, w) => s + w.amount, 0);

  return (
    <AdminLayout title="Partner Withdrawals">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Pending',  count: counts.pending  || 0, amount: totalPending,  color: 'text-amber-400', border: 'border-amber-500/20' },
          { label: 'Approved', count: counts.approved || 0, amount: totalApproved, color: 'text-blue-400',  border: 'border-blue-500/20' },
          { label: 'Paid',     count: counts.paid     || 0, amount: 0,             color: 'text-emerald-400', border: 'border-emerald-500/20' },
          { label: 'Rejected', count: counts.rejected || 0, amount: 0,             color: 'text-red-400',   border: 'border-red-500/20' },
        ].map(({ label, count, amount, color, border }) => (
          <Card key={label} className={`border ${border}`}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">{label}</p>
              <p className={`text-2xl font-bold ${color}`} data-testid={`count-${label.toLowerCase()}`}>{count}</p>
              {amount > 0 && <p className="text-xs text-muted-foreground mt-0.5">${amount.toFixed(2)} pending</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-3 mb-4">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {['', 'pending', 'approved', 'paid', 'rejected'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === s
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            }`}
            data-testid={`filter-${s || 'all'}`}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
          </button>
        ))}
        <Button variant="outline" size="sm" className="ml-auto h-8" onClick={fetch} data-testid="refresh-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : withdrawals.length === 0 ? (
            <div className="py-16 text-center">
              <ArrowDownToLine className="h-10 w-10 mx-auto mb-3 opacity-20" />
              <p className="text-muted-foreground">No withdrawal requests found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="withdrawals-table">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Partner</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Amount</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Network</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Wallet</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Note</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {withdrawals.map(w => {
                    const Icon = STATUS_ICON[w.status] || Clock;
                    const actions = ACTIONS[w.status] || [];
                    return (
                      <tr key={w.id} className="hover:bg-muted/20 transition-colors" data-testid={`wd-row-${w.id}`}>
                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                          <p>{fmtDate(w.created_at)}</p>
                          {w.processed_at && (
                            <p className="text-[11px] text-muted-foreground/60">Done: {fmtDate(w.processed_at)}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-foreground text-[13px]">{w.partner_name || '—'}</p>
                          {w.notes && <p className="text-[11px] text-muted-foreground truncate max-w-[140px]">{w.notes}</p>}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-bold text-foreground">
                          ${w.amount.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-[13px] text-muted-foreground">{w.network}</td>
                        <td className="px-4 py-3 font-mono text-[12px] text-muted-foreground max-w-[160px] truncate"
                          title={w.wallet_address}>{w.wallet_address}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-lg border ${STATUS_STYLE[w.status] || ''}`}>
                            <Icon className="h-3 w-3" />
                            {w.status.charAt(0).toUpperCase() + w.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground max-w-[120px] truncate">
                          {w.admin_note || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {actions.map(({ action, label, cls }) => (
                              <Button key={action} size="sm" variant="outline"
                                className={`h-7 text-[11px] px-2.5 ${cls}`}
                                onClick={() => openAction(w, action)}
                                data-testid={`action-${action}-${w.id}`}
                              >
                                {label}
                              </Button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Confirm Modal */}
      <Dialog open={!!actionModal} onOpenChange={open => !open && setActionModal(null)}>
        <DialogContent className="max-w-sm" data-testid="action-modal">
          <DialogHeader>
            <DialogTitle className="capitalize flex items-center gap-2">
              {actionModal?.action === 'approve'   && <CheckCircle2 className="h-5 w-5 text-blue-400" />}
              {actionModal?.action === 'mark_paid' && <DollarSign   className="h-5 w-5 text-emerald-400" />}
              {actionModal?.action === 'reject'    && <XCircle      className="h-5 w-5 text-red-400" />}
              {(actionModal?.action || '').replace('_', ' ')} Withdrawal
            </DialogTitle>
          </DialogHeader>

          {actionModal && (
            <div className="space-y-4 py-1">
              {/* Summary */}
              <div className="rounded-xl border border-border bg-muted/40 p-3 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Partner</span>
                  <span className="font-semibold">{actionModal.wd.partner_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-bold text-foreground">${actionModal.wd.amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Network</span>
                  <span className="font-medium">{actionModal.wd.network}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground shrink-0">Wallet</span>
                  <span className="font-mono text-[11px] text-right break-all">{actionModal.wd.wallet_address}</span>
                </div>
              </div>

              {/* Admin Note */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Admin Note (optional)</Label>
                <Input
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder={
                    actionModal.action === 'reject'    ? 'Reason for rejection...' :
                    actionModal.action === 'mark_paid' ? 'TX hash or payment ref...' :
                    'Any note...'
                  }
                  data-testid="action-note-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionModal(null)}>Cancel</Button>
            <Button
              onClick={handleAction}
              disabled={actioning}
              className={
                actionModal?.action === 'reject'    ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' :
                actionModal?.action === 'mark_paid' ? 'bg-emerald-600 text-white hover:bg-emerald-700' :
                'bg-blue-600 text-white hover:bg-blue-700'
              }
              data-testid="confirm-action-btn"
            >
              {actioning ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
